/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, {
  memo,
  useMemo,
  useState,
  ReactElement as ReactElement_
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  ApplicationInfoCard,
  Button,
  ApplicationStepsProgress,
  ListBoxContainer,
  ListBox,
  Collapse,
  IconsSolid
} from '@jkhy/vsg-design-system';
import {
  formatDate,
  htmlParser,
  isNullOrUndefined,
  joinValidElementsInString
} from '../../utils/helper';
import CurrentApplication_ from '../../data/models/CurrentRequestApplication';
import { ApplicationStatuses, QDPartyStatuses, RequestContext } from '../../utils/enum';
import ListItem_ from '../../data/models/ListItem';
import RequiredDocument_, { Attachment } from '../../data/models/RequiredDocumet';
import { endLoading, startLoading } from '../../redux/actions/loading-actions';
import BaseAPI from '../../data/base-api';
import CurrentApplicationsAPI from '../../data/current-applications-api';
import { setCurrentApplications } from '../../redux/actions/current-applications-actions';
import ConfirmationModal from '../common/confirmation-modal/confirmation-modal';
import FileUploadCardWrapper from '../common/file-upload/file-upload';
import { getCurrentApplicationsCountAction, renewalDocumentsCountFetched } from '../../redux/actions/counts-qd-enabled-actions';
import { getRequiredDocuments, getRequiredDocumentsSuccess } from '../../redux/actions/doc-actions';
import { setAntivirusResponse } from '../../redux/actions/anti-virus-actions';
import AntivirusResponseModel_ from '../../data/models/AntivirusResponse';
import { AntivirusResponseStatus as avStatuses_ } from '../../data/models/Enum';
import DocAPI from '../../data/doc-api';
import { DocDetail } from '../required-documents/required-documents';
import { AppState } from '../../redux/app-state';
import { AppDispatch } from '../../data/models/AppDispatch';

type ListBoxProps = {
  title?: {
    text: string,
    isBold?: boolean,
  },
  description: string | ReactElement_,
  iconSettings?: {
    icon: IconsSolid.IconDefinition,
    className: string,
  },
};

const enum StepValues {
  Pending = 0,
  WaitingOnCoBorrowers = 1,
  None = -1,
}

const enum ConfirmationModes {
  Decline = 'decline',
  Withdrawn = 'withdrawn'
}

const CurrentRequestApplication = ({ application, listItems, }: { application: CurrentApplication_, listItems: ListItem_[] }) => {
  const {
    borrowerName,
    dateStarted,
    contactName,
    contactPhone = '',
    contactEmail = '',
    expireInXDays,
    idFile,
    requestContext: context,
    portalApplicationStatusCode,
    applicationStatusStr,
    applicationNumber,
    guidqdApplication,
    idQDApplication,
    guidqdParty,
    partyStatus,
    urlGuarantorApp,
    urlResumeApp,
  } = application;

  // If application has been submitted
  const stepsNames = idFile
    ? listItems?.map(listItem => listItem.description)
    : ['Started', 'Waiting on Co-Applicant'];

  const [isCollapseExpand, setIsCollapseExpand] = useState<boolean>(false);
  const [confirmationMode, setConfirmationMode] = useState<ConfirmationModes>(null);
  const { allDocuments, allApplications, } = useSelector((state: AppState) => ({
    allDocuments: state.doc,
    allApplications: state.currentApplications,
  }));
  const [fileRef, setFileRef] = useState({});

  const isDeclineMode = confirmationMode === ConfirmationModes.Decline;

  const dispatch: AppDispatch = useDispatch();

  const currentStep = useMemo(() => {
    const secondCode = listItems?.find((listItem) => listItem.code === portalApplicationStatusCode)?.secondCode;

    switch (applicationStatusStr) {
      case ApplicationStatuses.Started: return StepValues.Pending;
      case ApplicationStatuses.WaitingOnCoApplicant: return StepValues.WaitingOnCoBorrowers;
      default:
        if (secondCode && idFile) {
          const [firstStep] = listItems;
          const { secondCode: firstStepSecondCode, } = firstStep;
          return parseInt(secondCode, 10) - parseInt(firstStepSecondCode, 10);
        }

        return StepValues.None;
    }
  }, [listItems, portalApplicationStatusCode, applicationStatusStr, idFile]);

  const { currentDocuments, documentsCount, } = useMemo(() => {
    const filteredDocuments = allDocuments.filter((document: RequiredDocument_) => !isNullOrUndefined(idFile) && document.idFile === idFile);

    return {
      currentDocuments: filteredDocuments,
      documentsCount: filteredDocuments.length,
    };
  }, [allDocuments, idFile]);

  const listBoxes = useMemo(() => {
    const primaryListBox: ListBoxProps[] = [ // Left part
      {
        title: {
          text: 'Primary Borrower:',
          isBold: true,
        },
        description: htmlParser(borrowerName),
      },
      {
        title: {
          text: 'Date Started:',
          isBold: true,
        },
        description: formatDate(dateStarted),
      }
    ];

    const primaryListBoxItem: ListBoxProps = {
      iconSettings: {
        icon: IconsSolid.faInfoCircle,
        className: 'text-primary',
      },
      description: '',
    };
    switch (context) {
      case RequestContext.Borrower:
        if (!isNullOrUndefined(expireInXDays) && expireInXDays >= 0) {
          primaryListBoxItem.description = expireInXDays === 0 ? 'Expires today.' : `Expires in ${expireInXDays} days.`;
        }
        break;
      case RequestContext.Guarantor:
      case RequestContext.LoanGuarantor:
        primaryListBoxItem.description = 'You have been added to this application.';
        break;
      default: break;
    }
    if (primaryListBoxItem?.description !== '') {
      primaryListBox.push(primaryListBoxItem);
    }

    const secondaryListBox: ListBoxProps[] = []; // Right part

    if (contactPhone) {
      secondaryListBox.push({
        iconSettings: {
          icon: IconsSolid.faIdCard,
          className: 'text-primary',
        },
        title: {
          text: contactName,
          isBold: true,
        },
        description: <a href={joinValidElementsInString(['tel', contactPhone], ':')} title={contactPhone}>{contactPhone}</a>,
      });
    }

    if (idFile && contactEmail) {
      secondaryListBox.push({
        iconSettings: {
          icon: IconsSolid.faEnvelope,
          className: 'text-primary',
        },
        title: {
          text: 'E-mail:',
          isBold: true,
        },
        description: <a href={joinValidElementsInString(['mailto', contactEmail], ':')} title={contactEmail}>{contactEmail}</a>,
      });
    }

    return [primaryListBox, secondaryListBox];
  }, [borrowerName, contactEmail, contactName, contactPhone, dateStarted, expireInXDays, idFile, context]);

  const withdrawApplication = async () => {
    try {
      dispatch(startLoading());

      const res = await CurrentApplicationsAPI.withdrawCurrentApplication(idQDApplication);
      if (res.errorCode <= 0 && idQDApplication) {
        const newApplications = allApplications.filter((app: CurrentApplication_) => app.idQDApplication !== idQDApplication);
        dispatch(setCurrentApplications(newApplications));
        dispatch(getCurrentApplicationsCountAction({ isFetched: true, count: newApplications.length, }));
      }
    } catch (err) {
      BaseAPI.onError(err);
    } finally {
      dispatch(endLoading());
      setConfirmationMode(null);
    }
  };

  const declineApplication = async () => {
    try {
      dispatch(startLoading());

      const res = await CurrentApplicationsAPI.declineCurrentCoBorrowerApplication(guidqdParty);
      if (res.errorCode <= 0 && guidqdParty) {
        const newApplications = allApplications.filter((app: CurrentApplication_) => app.guidqdParty !== guidqdParty);
        dispatch(setCurrentApplications(newApplications));
        dispatch(getCurrentApplicationsCountAction({ isFetched: true, count: newApplications.length, }));
      }
    } catch (err) {
      BaseAPI.onError(err);
    } finally {
      dispatch(endLoading());
      setConfirmationMode(null);
    }
  };

  const getButtons = (
    currentContext: RequestContext,
    appStatus: ApplicationStatuses,
    qdPartyStatus: QDPartyStatuses,
    guarantorAppUrl: string,
    borrowerAppUrl: string
  ) => {
    if (currentContext === RequestContext.Loan || currentContext === RequestContext.LoanGuarantor) {
      return null;
    }

    const guarantorBtnText = () => {
      switch (qdPartyStatus) {
        case QDPartyStatuses.Pending: return 'Start Application';
        case QDPartyStatuses.Accepted: return 'Continue Application';
        case QDPartyStatuses.Submitted: return 'View Application';
        default: return 'View Application';
      }
    };

    const buttonsSettings = {
      primary: {
        [RequestContext.Borrower]: {
          text: appStatus === ApplicationStatuses.Started ? 'Continue Application' : 'View Application',
          type: 'link',
          href: borrowerAppUrl,
          isVisible: true,
        },
        [RequestContext.Guarantor]: {
          text: guarantorBtnText(),
          type: 'link',
          href: guarantorAppUrl,
          isVisible: true,
        },
      },
      secondary: {
        [RequestContext.Borrower]: {
          text: 'Withdraw Application',
          type: 'button',
          action: () => setConfirmationMode(ConfirmationModes.Withdrawn),
          isVisible: appStatus === ApplicationStatuses.Started || appStatus === ApplicationStatuses.Approved,
        },
        [RequestContext.Guarantor]: {
          text: 'Decline Application',
          type: 'button',
          action: () => setConfirmationMode(ConfirmationModes.Decline),
          isVisible: qdPartyStatus === QDPartyStatuses.Pending,
        },
      },
    };

    return (
      <>
        {buttonsSettings.secondary[currentContext]?.isVisible && (
          <Button
            className="w-50 action-spacer"
            btnType="secondary"
            onClick={buttonsSettings.secondary[currentContext]?.action}
          >
            {buttonsSettings.secondary[currentContext]?.text}
          </Button>
        )}

        <Button
          className="w-50"
          link={{ path: buttonsSettings.primary[currentContext]?.href, title: buttonsSettings.primary[currentContext]?.text, isNativeLink: true, }}
        >
          {buttonsSettings.primary[currentContext]?.text}
        </Button>
      </>
    );
  };

  const getFileRef = (id: number) => {
    return fileRef[id];
  };

  const onUploadCallback = (docGuid: string, newAttachments: Attachment[]) => {
    const modifiedDocs = [...allDocuments].map(doc => {
      return docGuid === doc?.guid
        ? { ...doc, attachments: [...doc?.attachments, ...newAttachments], }
        : doc;
    });

    dispatch(getRequiredDocumentsSuccess(modifiedDocs));

    const hasThreat = newAttachments.find(a => (a.error === avStatuses_.FileNotFound || a.error === avStatuses_.ThreatFound));
    if (hasThreat) {
      const params: AntivirusResponseModel_ = { FileName: hasThreat.file.name, HasThreat: true, };
      dispatch(setAntivirusResponse(params));
    }
  };

  const onRemoveCallback = (docGuid: string, removedAttachment: Attachment) => {
    const modifiedDocs = [...allDocuments].map(doc => {
      if (docGuid !== doc.guid) return doc;

      const modifiedAttachmnets = [...doc.attachments].filter(a => {
        // remove incorrect file
        if (a?.file?.name === removedAttachment?.file?.name
          && a?.file?.size === a?.file.size
          && !removedAttachment.guid) {
          return false;
        }

        // remove uplaoded file
        return a.guid !== removedAttachment.guid;
      });
      return { ...doc, attachments: modifiedAttachmnets, };
    });

    dispatch(getRequiredDocumentsSuccess(modifiedDocs));
  };

  const onSubmitCallback = async () => {
    const { data: reqDocCount, } = await DocAPI.getAllRequestsCount();

    dispatch(renewalDocumentsCountFetched({ isFetched: true, count: reqDocCount, }));

    dispatch(getRequiredDocuments());
  };

  const getDocumentDetails = (document: RequiredDocument_) => {
    const {
      requiredFor,
      statementDate,
      portalDescription,
      documentDescription,
      hideDescription,
      hideStatementDate,
    } = document;

    const description = portalDescription || documentDescription;

    const requiredForText = requiredFor.split(',').map(item => item.trim().replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec)));

    const details: DocDetail[] = [{
      title: 'Required for:',
      maxVisibleTexts: requiredForText.length > 5 ? 5 : undefined,
      text: requiredForText,
    }];

    if (statementDate && !hideStatementDate) {
      details.push({ title: 'Statement Date:', text: formatDate(statementDate), });
    }

    if (description && !hideDescription) {
      details.push({ title: 'Description:', text: description, });
    }

    return details;
  };

  return (
    <ApplicationInfoCard
      dataUI={joinValidElementsInString(['application', applicationNumber, 'info', 'card'], '-')}
      className="mb-3"
      title={applicationNumber ? `Application #${applicationNumber}` : ''}
      buttonsRender={getButtons(context, applicationStatusStr, partyStatus, urlGuarantorApp, urlResumeApp)}
    >
      {currentStep > StepValues.None && (
        <ApplicationStepsProgress
          dataUI={joinValidElementsInString(['application', applicationNumber, 'steps'], '-')}
          className="mb-2"
          stepsNames={stepsNames}
          steps={stepsNames?.length}
          currentStep={currentStep}
          // eslint-disable-next-line react/jsx-one-expression-per-line
          description={(<><strong>Current step: </strong>{stepsNames[currentStep]}</>)}
        />
      )}

      <ListBoxContainer>
        {listBoxes.filter((listBox) => listBox.length > 0).map((listBox, index) => {
          const listBoxColumns = listBox.map(box => box?.title?.text);
          const key = joinValidElementsInString([applicationNumber, guidqdApplication, guidqdParty, idFile, ...listBoxColumns], '-');

          return (
            <ListBox
              dataUI={joinValidElementsInString(['application', applicationNumber, 'list', 'box', index], '-')}
              key={key}
              boxItems={listBox}
            />
          );
        })}
      </ListBoxContainer>

      {documentsCount > 0 && (
        <Collapse
          dataUI={joinValidElementsInString(['application', applicationNumber, 'required', 'documents', 'collapse'], '-')}
          title="Required Documents"
          expanded={isCollapseExpand}
          onClick={() => setIsCollapseExpand(prevState => !prevState)}
          badgeContent={documentsCount}
        >
          {currentDocuments?.map((document: RequiredDocument_, index: number) => {
            const {
              id,
              documentType,
              portalDocumentName,
            } = document;

            return (
              <FileUploadCardWrapper
                key={`${id}`}
                dataUI={joinValidElementsInString(['application', applicationNumber, 'document', index], '-')}
                keyValue={`${id}`}
                className="mt-1"
                title={portalDocumentName || documentType}
                details={getDocumentDetails(document)}
                isMultiple
                onSubmitFileCallback={() => onSubmitCallback()}
                onFileUploadCallback={onUploadCallback}
                onFileRemoveCallback={onRemoveCallback}
                onRef={(ref) => {
                  setFileRef((prevState) => ({ ...prevState, [id]: ref, }));
                }}
                getRef={getFileRef}
                document={document}
                type="compact"
                additionalInfoText="Each file must be less than 50MB. The total size of all files cannot be greater than 100MB."
              />
            );
          })}
        </Collapse>
      )}

      {confirmationMode && (
        <ConfirmationModal
          isOpen={!!confirmationMode}
          onClose={() => setConfirmationMode(null)}
          onSubmit={isDeclineMode ? declineApplication : withdrawApplication}
          text={isDeclineMode
            ? 'Are you sure you want to be removed from this application?'
            : 'Are you sure you want to withdraw your application?'}
          title={isDeclineMode ? 'Decline Application' : 'Withdraw'}
        />
      )}
    </ApplicationInfoCard>
  );
};

export default memo(CurrentRequestApplication);
